import React, { useEffect, useState } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import api from '../../services/api';
import { useNavigate } from 'react-router-dom';
import styles from './OfferList.module.css';
import { usePermission } from '../../hooks/usePermissions';
import FullscreenModal from '../../components/common/FullscreenModal/FullscreenModal';
import AddOffer from './AddOffer';
import { formatAdminDate } from '../../utils/datetime';



const EditIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 20h9" />
        <path d="M16.5 3.5a2.12 2.12 0 1 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
);

const TrashIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="3 6 5 6 21 6" />
        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
        <path d="M10 11v6" />
        <path d="M14 11v6" />
        <path d="M9 6V4h6v2" />
    </svg>
);

const OfferList = () => {
    const { hasPermission } = usePermission();
    const navigate = useNavigate();
    const [offers, setOffers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedOfferId, setSelectedOfferId] = useState(null);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const itemsPerPage = 10;

    // Sorting
    const [sortKey, setSortKey] = useState('offer_id');
    const [sortOrder, setSortOrder] = useState('desc');

    // Filters
    const [searchTerm, setSearchTerm] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    const handleSort = (key) => {
        if (sortKey === key) {
            setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortKey(key);
            setSortOrder('asc');
        }
    };

    const fetchOffers = (page = currentPage) => {
        setLoading(true);
        api.get('/offer')
            .then((response) => {
                let resultsArray = response?.result || response?.data || response || [];
                if (!Array.isArray(resultsArray)) resultsArray = [];

                let filtered = [...resultsArray];
                if (searchTerm) {
                    const lSearch = searchTerm.toLowerCase();
                    filtered = filtered.filter((item) => {
                        const hasName = item.offer_name && item.offer_name.toLowerCase().includes(lSearch);
                        const hasCode = item.offer_code && item.offer_code.toLowerCase().includes(lSearch);
                        const hasId = item.offer_id && item.offer_id.toString().includes(lSearch);
                        return hasName || hasCode || hasId;
                    });
                }

                if (fromDate) {
                    const from = new Date(fromDate);
                    filtered = filtered.filter((item) => item.active_timestamp && new Date(item.active_timestamp) >= from);
                }
                if (toDate) {
                    const to = new Date(toDate);
                    to.setHours(23, 59, 59, 999);
                    filtered = filtered.filter((item) => item.active_timestamp && new Date(item.active_timestamp) <= to);
                }

                filtered.sort((a, b) => {
                    let aVal = a[sortKey];
                    let bVal = b[sortKey];
                    if (aVal === null || aVal === undefined) aVal = '';
                    if (bVal === null || bVal === undefined) bVal = '';
                    const aNum = Number(aVal);
                    const bNum = Number(bVal);
                    if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
                        return sortOrder === 'asc' ? aNum - bNum : bNum - aNum;
                    }
                    return sortOrder === 'asc'
                        ? String(aVal).localeCompare(String(bVal))
                        : String(bVal).localeCompare(String(aVal));
                });

                const totalFilteredPages = Math.ceil(filtered.length / itemsPerPage) || 1;
                let safePage = page;
                if (safePage > totalFilteredPages) {
                    safePage = totalFilteredPages;
                    setCurrentPage(safePage);
                }
                const startIndex = (safePage - 1) * itemsPerPage;
                const sliced = filtered.slice(startIndex, startIndex + itemsPerPage);

                setOffers(sliced);
                setTotalPages(totalFilteredPages);
            })
            .catch((error) => {
                console.error('Error fetching offers:', error);
                toast.error('Failed to fetch offers');
                setOffers([]);
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchOffers(currentPage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, sortKey, sortOrder]);

    const handleSearch = () => {
        setCurrentPage(1);
        fetchOffers(1);
    };

    const handleDelete = (id) => {
        if (!window.confirm('Are you sure you want to delete this offer?')) return;
        api.delete(`/offer/${id}`)
            .then(() => {
                toast.success('Offer deleted successfully');
                fetchOffers(currentPage);
            })
            .catch((error) => {
                const errMsg = error?.data?.message || error?.message || 'Failed to delete';
                toast.error(`Delete Error: ${errMsg}`);
            });
    };

    const formatDate = (dateStr) => formatAdminDate(dateStr, 'N/A');

    return (
        <div className={styles.container}>
            <ToastContainer position="bottom-right" autoClose={2000} />

            <div className={styles.pageHeader}>
                <h1 className={styles.title}>Offers List</h1>
                {hasPermission("CREATE_OFFERS") && (
                    <button className={styles.addBtn} onClick={() => {
                        setSelectedOfferId(null);
                        setIsModalOpen(true);
                    }}>
                        + Add Offer
                    </button>
                )}
            </div>

            <div className={styles.card}>
                <div className={styles.filtersBar}>
                    <div className={styles.dateFilters}>
                        <div className={styles.dateGroup}>
                            <label className={styles.dateLabel}>From</label>
                            <input
                                type="date"
                                className={styles.dateInput}
                                value={fromDate}
                                onChange={(e) => setFromDate(e.target.value)}
                            />
                        </div>
                        <div className={styles.dateGroup}>
                            <label className={styles.dateLabel}>To</label>
                            <input
                                type="date"
                                className={styles.dateInput}
                                value={toDate}
                                onChange={(e) => setToDate(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className={styles.searchGroup}>
                        <label className={styles.dateLabel}>Search</label>
                        <div className={styles.searchInputRow}>
                            <input
                                type="text"
                                className={styles.searchInput}
                                placeholder="Search by ID, Name, Code..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                            />
                            <button className={styles.searchBtn} onClick={handleSearch}>
                                Search
                            </button>
                        </div>
                    </div>
                </div>

                <div className={styles.tableWrapper}>
                    {loading ? (
                        <div className={styles.loadingState}>
                            <div className={styles.spinner}></div>
                            <span>Loading offers...</span>
                        </div>
                    ) : (
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th onClick={() => handleSort('offer_id')} className={styles.sortable}>
                                        ID {sortKey === 'offer_id' ? <span className={styles.sortIconActive}>⇅</span> : <span className={styles.sortIcon}>⇅</span>}
                                    </th>
                                    <th onClick={() => handleSort('offer_name')} className={styles.sortable}>
                                        Name / Code {sortKey === 'offer_name' ? <span className={styles.sortIconActive}>⇅</span> : <span className={styles.sortIcon}>⇅</span>}
                                    </th>
                                    <th>Value / Discount</th>
                                    <th onClick={() => handleSort('consumed_count')} className={styles.sortable}>
                                        Usage {sortKey === 'consumed_count' ? <span className={styles.sortIconActive}>⇅</span> : <span className={styles.sortIcon}>⇅</span>}
                                    </th>
                                    <th onClick={() => handleSort('active_timestamp')} className={styles.sortable}>
                                        Valid From {sortKey === 'active_timestamp' ? <span className={styles.sortIconActive}>⇅</span> : <span className={styles.sortIcon}>⇅</span>}
                                    </th>
                                    <th onClick={() => handleSort('deactive_timestamp')} className={styles.sortable}>
                                        Valid To {sortKey === 'deactive_timestamp' ? <span className={styles.sortIconActive}>⇅</span> : <span className={styles.sortIcon}>⇅</span>}
                                    </th>
                                    <th onClick={() => handleSort('status')} className={styles.sortable}>
                                        Status {sortKey === 'status' ? <span className={styles.sortIconActive}>⇅</span> : <span className={styles.sortIcon}>⇅</span>}
                                    </th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {offers.length > 0 ? (
                                    offers.map((row, idx) => (
                                        <tr key={row.offer_id ?? idx}>
                                            <td>{row.offer_id}</td>
                                            <td>
                                                <div className={styles.nameText}>{row.offer_name}</div>
                                                <div className={styles.codeText}>{row.offer_code}</div>
                                            </td>
                                            <td>
                                                <div className={styles.valueText}>
                                                    {row.calculation_type === 'percentage'
                                                        ? `${row.offer_percentage}%`
                                                        : `Rs ${row.offer_value}`}
                                                    <span className={styles.calcType}> ({row.calculation_type})</span>
                                                </div>
                                                {(row.minimum_order_amount > 0 || row.max_value > 0) && (
                                                    <div className={styles.limitText}>
                                                        {row.minimum_order_amount > 0 && `Min: Rs ${row.minimum_order_amount}`}
                                                        {row.minimum_order_amount > 0 && row.max_value > 0 && ' | '}
                                                        {row.max_value > 0 && `Max: Rs ${row.max_value}`}
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                <div className={styles.usageText}>
                                                    {row.consumed_count || 0} / {row.max_count === 0 ? 'INF' : row.max_count}
                                                </div>
                                                <div className={styles.limitText}>Val: Rs {row.consumed_value || 0}</div>
                                            </td>
                                            <td>{formatDate(row.active_timestamp)}</td>
                                            <td>{formatDate(row.deactive_timestamp)}</td>
                                            <td>
                                                <span className={`${styles.statusBadge} ${row.status?.toLowerCase() === 'active'
                                                    ? styles.statusActive
                                                    : styles.statusInactive
                                                    }`}>
                                                    {(row.status || 'UNKNOWN').toUpperCase()}
                                                </span>
                                            </td>
                                            <td>
                                                <div className={styles.actionBtns}>
                                                    {hasPermission("MODIFY_OFFERS") && (
                                                        <button
                                                            className={`${styles.iconBtn} ${styles.edit}`}
                                                            title="Edit"
                                                            onClick={() => {
                                                                setSelectedOfferId(row.offer_id);
                                                                setIsModalOpen(true);
                                                            }}
                                                        >
                                                            <EditIcon />
                                                        </button>
                                                    )}
                                                    {hasPermission("DELETE_OFFERS") && (
                                                        <button
                                                            className={`${styles.iconBtn} ${styles.delete}`}
                                                            title="Delete"
                                                            onClick={() => handleDelete(row.offer_id)}
                                                        >
                                                            <TrashIcon />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={8} className={styles.emptyState}>
                                            No offers found.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>

                <div className={styles.pagination}>
                    <div className={styles.paginationInfo}>
                        Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, offers.length * totalPages)} of {offers.length * totalPages} entries
                    </div>
                    <div className={styles.paginationControls}>
                        <button
                            className={styles.pageBtn}
                            disabled={currentPage === 1}
                            onClick={() => setCurrentPage((p) => p - 1)}
                        >
                            &lt;
                        </button>

                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => {
                            if (
                                p === 1 ||
                                p === totalPages ||
                                (p >= currentPage - 1 && p <= currentPage + 1)
                            ) {
                                return (
                                    <button
                                        key={p}
                                        className={`${styles.pageBtn} ${currentPage === p ? styles.active : ""}`}
                                        onClick={() => setCurrentPage(p)}
                                    >
                                        {p}
                                    </button>
                                );
                            }
                            if (p === currentPage - 2 || p === currentPage + 2) {
                                return <span key={p} className={styles.paginationEllipsis}>...</span>;
                            }
                            return null;
                        })}

                        <button
                            className={styles.pageBtn}
                            disabled={currentPage >= totalPages}
                            onClick={() => setCurrentPage((p) => p + 1)}
                        >
                            &gt;
                        </button>
                    </div>
                </div>
            </div>
            {isModalOpen && (
                <FullscreenModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    title={selectedOfferId ? "Edit Offer" : "Create New Offer"}
                    maxWidth="800px"
                    disableBackdropClick={true}
                >
                    <AddOffer
                        offerId={selectedOfferId}
                        onSubmitted={() => {
                            setIsModalOpen(false);
                            fetchOffers(currentPage);
                        }}
                        onCancel={() => setIsModalOpen(false)}
                    />
                </FullscreenModal>
            )}
        </div>
    );
};

export default OfferList;
